import { useMemo } from 'react'
import { type IntlShape, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Accordion from 'LuxKit/Accordion'

export interface LeLiveFaq {
  id: string
  question: string
  answer: string
}

export function getLeLiveFaqs(intl: IntlShape): Array<LeLiveFaq> {
  return [
    {
      id: 'faq-about-event',
      question: intl.formatMessage({
        id: 'GU2b6SE2s4Y8yablZbzn',
        defaultMessage: 'What is Luxury Escapes Live?',
        description: 'FAQ question explaining what Luxury Escapes Live is',
      }),
      answer: intl.formatMessage({
        id: 'pnCUmyX1m_rbySZ2QBSe',
        defaultMessage: 'Luxury Escapes Live is a two-day celebration of travel taking over the Royal Exhibition Building on 16–17 May 2026. Expect exclusive offers, thousands of prizes, expert advice and bucket-list destination inspiration all under one roof.',
        description: 'FAQ answer describing what the Luxury Escapes Live event is',
      }),
    },
    {
      id: 'faq-tickets',
      question: intl.formatMessage({
        id: 'yRh7mOI6yJi2uhrVpUqb',
        defaultMessage: 'How do I get my free tickets?',
        description: 'FAQ question about how to obtain free LE Live event tickets',
      }),
      answer: intl.formatMessage({
        id: 'akWUClJaZurDDLHyTSUd',
        defaultMessage: 'Luxury Escapes Live is free for members. Log in to your Luxury Escapes account, choose your preferred day and number of tickets, then submit your registration to receive confirmation by email.',
        description: 'FAQ answer with steps to register for free LE Live event tickets',
      }),
    },
    {
      id: 'faq-travel-expo-difference',
      question: intl.formatMessage({
        id: 'egicC0iPH1TXhJWkjCxP',
        defaultMessage: 'What makes Luxury Escapes Live different from another travel expo?',
        description: 'FAQ question about what sets Luxury Escapes Live apart from other travel expos',
      }),
      answer: intl.formatMessage({
        id: '2MqPrP33Kbmbeus0gvN1',
        defaultMessage: 'This is more than just another travel expo. Luxury Escapes Live gives you access to exclusive show-only offers, insider perks and limited-time value you will not find online or anywhere else.',
        description: 'FAQ answer describing what makes Luxury Escapes Live different from other travel expos',
      }),
    },
    {
      id: 'faq-offers-prizes',
      question: intl.formatMessage({
        id: 'ad3UqqV7ipixoE36N0Q2',
        defaultMessage: 'What kinds of offers and prizes can I expect?',
        description: 'FAQ question about the offers and prizes available at Luxury Escapes Live',
      }),
      answer: intl.formatMessage({
        id: 'tcVHIvYpdQq3cRvus0DN',
        defaultMessage: 'Expect limited-time travel deals, exclusive event-only offers, unbeatable value and thousands of prizes and giveaways across the weekend. There will also be surprise moments and spontaneous wins announced during the event.',
        description: 'FAQ answer about the offers and prizes available at Luxury Escapes Live',
      }),
    },
    {
      id: 'faq-destinations',
      question: intl.formatMessage({
        id: 'gKb0ig-lLaIQbSDX52U0',
        defaultMessage: 'What destinations and travel experiences will be featured?',
        description: 'FAQ question about destinations and travel experiences featured at Luxury Escapes Live',
      }),
      answer: intl.formatMessage({
        id: 'zoHfcO27aB2YcYS7-FQ0',
        defaultMessage: 'Luxury Escapes Live will showcase everything from Maldives overwater stays and Bali beach breaks to city escapes and once-in-a-lifetime experiences around the world. You will also be able to explore curated partner showcases, emerging hotspots and fresh holiday ideas in one place.',
        description: 'FAQ answer about destinations and travel experiences featured at Luxury Escapes Live',
      }),
    },
    {
      id: 'faq-weekend-experiences',
      question: intl.formatMessage({
        id: 'zxXKB8rmH-VQow6NhoI9',
        defaultMessage: 'What can I expect across the weekend?',
        description: 'FAQ question about what guests can expect across the Luxury Escapes Live weekend',
      }),
      answer: intl.formatMessage({
        id: 'DN-onQ8lXapN9OXSVsw4',
        defaultMessage: 'Across the weekend you can explore the Mastercard Main Stage, the Passport Card travel marketplace, the Discover Hong Kong market, the Wine Selectors cellar door experience and Lavazza coffee stations. A full schedule of celebrity appearances and additional programming will also be announced closer to the event.',
        description: 'FAQ answer about what guests can expect across the Luxury Escapes Live weekend',
      }),
    },
    {
      id: 'faq-societe-lounge',
      question: intl.formatMessage({
        id: '9casaWZcaO_DHmkaxJNA',
        defaultMessage: 'What is the Société Lounge?',
        description: 'FAQ question about the Société Lounge at Luxury Escapes Live',
      }),
      answer: intl.formatMessage({
        id: 'mHSSwQRhGCB2tGvvsdWi',
        defaultMessage: 'The Société Lounge, presented by Cathay Pacific, is an exclusive space for Société and LuxPlus+ members to relax and enjoy a premium event experience during Luxury Escapes Live.',
        description: 'FAQ answer about the Société Lounge at Luxury Escapes Live',
      }),
    },
  ]
}

function LeLiveFaqs() {
  const intl = useIntl()
  const faqs = useMemo(() => getLeLiveFaqs(intl), [intl])

  return (
    <Sheet background="neutral-eight" padding="64 0" id="faqs">
      <LayoutContainer variant="medium">
        <Group direction="vertical" gap={40}>
          <Heading variant="heading2" as="h2" align="center">
            {intl.formatMessage({
              id: 'CQ2Kf0DRmAiH5PKiFRuL',
              defaultMessage: 'Frequently asked questions',
              description: 'Main heading for the frequently asked questions section on the LE Live event landing page',
            })}
          </Heading>

          <Group direction="vertical" gap={0}>
            {faqs.map(faq => (
              <Accordion
                key={faq.id}
                id={faq.id}
                heading={faq.question}
                size="large"
              >
                <BodyText variant="medium" colour="neutral-two">
                  {faq.answer}
                </BodyText>
              </Accordion>
            ))}
          </Group>
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LeLiveFaqs
